<script setup lang="ts">
import { computed } from 'vue'

import type { AlertDraft } from '../models/alert.types'
import type { AlertChannelType, ChannelAvailability } from '../models/channel.types'
import type { AlertValidationKey } from '../service/alertValidator'

import { useI18n } from '@/shared/i18n'
import { NotificationProviderIcon } from '@/shared/notifications'
import { Icon, Input, MultiSelect, Tooltip } from '@/shared/ui'

const props = defineProps<{
  draft: AlertDraft
  availability: readonly ChannelAvailability[]
  error: AlertValidationKey | null
  disabled: boolean
}>()
const emit = defineEmits<{
  'update:channels': [channels: AlertChannelType[]]
  'update:emails': [emails: string[]]
  'update:numbers': [numbers: string[]]
}>()
const { t } = useI18n()
const channelOrder: readonly AlertChannelType[] = ['mail', 'whatsapp', 'slack', 'teams']

const mailOptions = computed(() => optionsFor('mail', props.draft.recipientEmails))
const whatsappOptions = computed(() => optionsFor('whatsapp', props.draft.whatsappNumbers))
const connectedChannels = computed(() =>
  (['slack', 'teams'] as const).filter((channel) => props.draft.channels.includes(channel)),
)
const analysisWindow = computed(() => {
  if (!props.draft.window) return t('alerts.summary.empty')
  return props.draft.window === 'custom'
    ? t('alerts.wizard.customWindow', { days: props.draft.customWindowDays ?? 0 })
    : t(`alerts.window.${props.draft.window}`)
})

function info(channel: AlertChannelType): ChannelAvailability {
  return (
    props.availability.find((item) => item.channel === channel) ?? {
      channel,
      configured: false,
      turnedOn: false,
      values: [],
      selectable: false,
    }
  )
}

function optionsFor(channel: AlertChannelType, selected: readonly string[]) {
  return [...new Set([...info(channel).values, ...selected])].map((value) => ({
    value,
    label: value,
  }))
}

function disabledReason(channel: AlertChannelType): string {
  const availability = info(channel)
  if (!availability.configured) return t('alerts.notify.notConfigured')
  if (!availability.turnedOn) return t('alerts.notify.turnedOff')
  return ''
}

function toggle(channel: AlertChannelType): void {
  const availability = info(channel)
  if (props.disabled || !availability.selectable) return
  emit(
    'update:channels',
    props.draft.channels.includes(channel)
      ? props.draft.channels.filter((item) => item !== channel)
      : [...props.draft.channels, channel],
  )
}
</script>

<template>
  <div class="notify-step grid gap-md py-sm">
    <div class="notify-step__channels grid grid-cols-4 gap-2xs max-sm:grid-cols-2">
      <Tooltip v-for="channel in channelOrder" :key="channel" :text="disabledReason(channel)">
        <button
          type="button"
          class="notify-step__tile relative flex w-full flex-col items-center justify-center gap-2xs rounded-md border px-0 py-2.5 disabled:cursor-not-allowed disabled:opacity-50"
          :class="
            draft.channels.includes(channel)
              ? 'is-selected border-info bg-info-soft text-info'
              : 'border-border-strong bg-surface-sunken text-fg'
          "
          :disabled="disabled || !info(channel).selectable"
          :aria-pressed="draft.channels.includes(channel)"
          @click="toggle(channel)"
        >
          <Icon
            v-if="draft.channels.includes(channel)"
            class="notify-step__selected absolute -top-1.5 -right-1.5 grid h-4 w-4 place-items-center rounded-full bg-info text-surface"
            name="check"
            :size="12"
          />
          <NotificationProviderIcon :provider="channel" :size="20" />
          <span>{{ t(`alerts.notify.channel.${channel}`) }}</span>
        </button>
      </Tooltip>
    </div>
    <p v-if="error" class="notify-step__error m-0 text-[0.75rem] text-danger">{{ t(error) }}</p>

    <div
      v-if="draft.channels.includes('mail')"
      class="notify-step__recipient rounded-md border border-border p-sm"
    >
      <MultiSelect
        :model-value="draft.recipientEmails"
        :options="mailOptions"
        :label="t('alerts.notify.emailRecipients')"
        :placeholder="t('alerts.notify.addMore')"
        searchable
        :disabled="disabled"
        @update:model-value="emit('update:emails', $event)"
      />
    </div>
    <div
      v-if="draft.channels.includes('whatsapp')"
      class="notify-step__recipient rounded-md border border-border p-sm"
    >
      <MultiSelect
        :model-value="draft.whatsappNumbers"
        :options="whatsappOptions"
        :label="t('alerts.notify.whatsappRecipients')"
        :placeholder="t('alerts.notify.addMore')"
        searchable
        :disabled="disabled"
        @update:model-value="emit('update:numbers', $event)"
      />
    </div>
    <div
      v-for="channel in connectedChannels"
      :key="channel"
      class="notify-step__recipient rounded-md border border-border p-sm"
    >
      <Input
        :model-value="info(channel).values.join(', ')"
        :label="t('alerts.notify.webhook', { channel: t(`alerts.notify.channel.${channel}`) })"
        readonly
      />
    </div>

    <p
      class="notify-step__analysis m-0 flex items-center gap-sm rounded-md bg-info-soft p-md text-info"
    >
      <Icon name="clock" :size="16" />
      <i18n-t keypath="alerts.wizard.analyzeSummary" scope="global" tag="span">
        <template #window
          ><strong>{{ analysisWindow }}</strong></template
        >
      </i18n-t>
    </p>
  </div>
</template>

<style scoped>
.notify-step .notify-step__channels :deep(.ui-tooltip-trigger) {
  width: 100%;
}
</style>
